import { useState, useRef } from "react";
import { useSearchParams } from "react-router";
import { useCategories } from "../api/swr/useCategories";
import { useTiers } from "../api/swr/useTiers";
import { Button } from "~/atoms/button";
import { TierModal } from "../organisms/TierModal";
import HeadingPage from "../molecules/HeadingPage";
import { CenterSpinner } from "../atoms/spinner";
import cn from "classnames";
import { useTranslation } from "react-i18next";
import { getChartData } from "../helpers/chart";
import ChartDocuments from "../organisms/ChartDocuments";
import { ChartCrossCategoryContext } from "../organisms/DocumentCard";
import { useUserContext } from "../hooks/useUserContext";
import { PlatformRole } from "../config/types";
import { useDocumentVersions } from "../api/v2/useDocumentVersions";
import { ChevronRight, Filter as FilterIcon, Info } from "lucide-react";
import { DocumentMetadataSheet } from "~/molecules/DocumentMetadataSheet";
import { Switch } from "~/atoms/switch";
import { Label } from "~/atoms/label";
import { ManageCategoriesButton } from "~/molecules/ManageCategoriesButton";
import { TagsModal } from "~/organisms/TagsModal";
import { useDocumentVersionFilters } from "~/hooks/useDocumentVersionFilters";
import { useStickyHorizontalScrollbar } from "~/hooks/useStickyHorizontalScrollbar";
import {
  filterDocumentVersions,
  type DateFilterKey,
  type MultiSelectFilterKey,
} from "~/helpers/documentFilters";
import { FilterBar, FiltersSidebar } from "~/organisms/library/FilterBar";

const CHART_MULTI_SELECT_FILTER_KEYS = [
  "status",
  "category",
  "tag",
  "owner",
  "publisher",
] as const satisfies readonly MultiSelectFilterKey[];

const CHART_DATE_FILTER_KEYS = [
  "publishedAt",
  "adoptedAt",
] as const satisfies readonly DateFilterKey[];

const Chart = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: categories } = useCategories();
  const { data: tiers } = useTiers();
  const { data: documentVersions, mutate: mutateDocumentVersions } =
    useDocumentVersions({ activeVersion: true });
  const { user } = useUserContext();
  const { t } = useTranslation();

  const canManageFramework =
    user.role === PlatformRole.superAdmin ||
    user.role === PlatformRole.coordinator;

  const [selectedId, setSelectedId] = useState<number>();
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [showCategories, setShowCategories] = useState(true);

  const filters = useDocumentVersionFilters({
    documentVersions: documentVersions ?? [],
    searchParams,
    setSearchParams,
  });

  const chartFilters = {
    ...filters,
    multiSelectFilterKeys: CHART_MULTI_SELECT_FILTER_KEYS,
    dateFilterKeys: CHART_DATE_FILTER_KEYS,
  };

  const containerRef = useRef<HTMLDivElement>(null);
  const documentRefsMap = useRef<Map<number, HTMLDivElement>>(new Map());

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const stickyScrollRef = useRef<HTMLDivElement>(null);
  const scrollbarTrackRef = useRef<HTMLDivElement>(null);

  useStickyHorizontalScrollbar({
    targetRef: scrollContainerRef,
    barRef: stickyScrollRef,
    trackRef: scrollbarTrackRef,
    deps: [categories, tiers],
  });

  if (!documentVersions || !categories || !tiers) {
    return <CenterSpinner withText />;
  }

  const preFilteredCategories =
    user.role === PlatformRole.reader || user.role === PlatformRole.contributor
      ? categories.filter((d) => user.categories?.some((ud) => ud.id === d.id))
      : categories;

  const filteredDocumentVersions = filterDocumentVersions(
    documentVersions,
    chartFilters.activeFilters,
  );
  const chartDocumentVersions = filteredDocumentVersions.filter(
    (v) => v.document.tier !== null,
  );

  const selectedCategoryIds = chartFilters.getInValues("category");
  const visibleCategories =
    selectedCategoryIds.length > 0
      ? preFilteredCategories.filter((c) =>
          selectedCategoryIds.includes(c.id.toString()),
        )
      : preFilteredCategories;

  const categoryColumns = visibleCategories.map((category) => ({
    category,
    columns: getChartData(
      chartDocumentVersions.filter(
        (v) => v.document.category.id === category.id,
      ),
    ),
  }));

  const selectedVersion = selectedId
    ? chartDocumentVersions.find((v) => v.document.id === selectedId)
    : undefined;

  return (
    <>
      <div className="space-y-4 px-6 py-7">
        <HeadingPage title={t(($) => $.chart.header)} />

        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => setIsFiltersOpen((v) => !v)}
              className="shrink-0"
            >
              <FilterIcon />
              {t(($) => $.commons.filter.title)}
              <ChevronRight
                className={cn(
                  "transition-transform",
                  isFiltersOpen && "rotate-90",
                )}
              />
            </Button>
            <div className="flex items-center gap-2 pl-2">
              <Switch
                id="show-categories"
                checked={showCategories}
                onCheckedChange={setShowCategories}
              />
              <Label htmlFor="show-categories">
                {t(($) => $.chart.showCategories)}
              </Label>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {(user.role === PlatformRole.coordinator ||
              user.role === PlatformRole.superAdmin) && (
              <TagsModal
                trigger={
                  <Button variant="outline">
                    {t(($) => $.modal.tags.manageTags)}
                  </Button>
                }
              />
            )}
            {canManageFramework && (
              <>
                <ManageCategoriesButton />
                <TierModal
                  trigger={
                    <Button variant="outline">
                      {`${t(($) => $.commons.buttons.create)} ${t(($) => $.commons.document.tier)}`}
                    </Button>
                  }
                />
              </>
            )}
          </div>
        </div>

        <div
          className={cn("flex items-start", isFiltersOpen ? "gap-4" : "gap-0")}
        >
          <aside
            className={cn(
              "shrink-0 overflow-hidden transition-[width,opacity] duration-200",
              isFiltersOpen ? "w-80 opacity-100" : "w-0 opacity-0",
            )}
          >
            <div
              className={cn(
                "h-full w-80 origin-left transition-transform duration-200",
                isFiltersOpen
                  ? "scale-x-100"
                  : "pointer-events-none scale-x-95",
              )}
            >
              <FiltersSidebar filters={chartFilters} />
            </div>
          </aside>

          <div className="min-w-0 flex-1 space-y-2">
            <FilterBar filters={chartFilters} />

            <div
              ref={scrollContainerRef}
              className="relative flex [scrollbar-width:none] overflow-x-auto pb-7 [&::-webkit-scrollbar]:hidden"
            >
              <div className="bg-background sticky left-0 z-20 flex w-64 shrink-0 flex-col pl-7">
                <div className="h-9" />
                {tiers.map(({ id, title, description }) => (
                  <div
                    className="even:bg-accent/70 odd:bg-background flex h-44 flex-col justify-center pl-4"
                    key={id}
                  >
                    <div className="flex items-center gap-2 pr-4">
                      <h3 className="line-clamp-2 flex-1 font-medium wrap-break-word">
                        {title}
                      </h3>
                      {canManageFramework && (
                        <TierModal
                          tierId={id}
                          trigger={
                            <Button variant="ghost" size="icon">
                              <Info className="size-5 opacity-50" />
                            </Button>
                          }
                        />
                      )}
                    </div>
                    <div className="text-muted-foreground text-sm">
                      {description}
                    </div>
                  </div>
                ))}
              </div>

              <div className="relative flex pr-7" ref={containerRef}>
                <ChartCrossCategoryContext.Provider
                  value={{
                    documentVersions: chartDocumentVersions,
                    showCrossCategoryLinksTooltip: showCategories,
                  }}
                >
                  {!showCategories ? (
                    <ChartDocuments
                      columns={getChartData(chartDocumentVersions)}
                      tiers={tiers}
                      documentRefsMap={documentRefsMap}
                      containerRef={containerRef}
                      setSelectedId={setSelectedId}
                      canManage={canManageFramework}
                      onReorder={() => void mutateDocumentVersions()}
                    />
                  ) : (
                    categoryColumns.map(({ category, columns }) => (
                      <ChartDocuments
                        key={category.id}
                        columns={columns}
                        tiers={tiers}
                        documentRefsMap={documentRefsMap}
                        containerRef={containerRef}
                        setSelectedId={setSelectedId}
                        canManage={canManageFramework}
                        onReorder={() => void mutateDocumentVersions()}
                        category={category}
                      />
                    ))
                  )}
                </ChartCrossCategoryContext.Provider>
              </div>
            </div>

            <div
              ref={stickyScrollRef}
              className="fixed bottom-0 z-30 hidden overflow-x-auto overflow-y-hidden"
            >
              <div ref={scrollbarTrackRef} className="h-4" />
            </div>
          </div>
        </div>
      </div>

      {selectedVersion && (
        <DocumentMetadataSheet
          documentId={selectedVersion.document.id}
          documentVersionId={selectedVersion.id}
          open={true}
          setOpen={() => setSelectedId(undefined)}
          onSave={mutateDocumentVersions}
        />
      )}
    </>
  );
};

export default Chart;
